import {tableClass} from "./library/ShortCodes";
import {HardwareGroup} from "./HardwareGroup";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {colCount} from "./HardwareTable";
import {useProjectId} from "../ProjectName";
import {orderByAscending, useAsync2} from "nate-react-api-helpers";
import {useReload} from "./ReloadContext";
import {EditCell} from "./library/EditCell";
import {Tooltip} from "@mui/material";
import {useHwAPI} from "./HardwareGeneric";
import {useAlternative} from "../../quote/alternative/Alternative";
import {HwEditingContext} from "./HwEditingContextProvider";
import {ChangePreviewContext} from "../ShopDrawingChangePreview";
import {HardwareViewModeContext, ViewMode} from "../../quote/hardware/HardwareViewMode";
import {HardwareGroupWithContent, HwOpening} from "../../../../api/Hardware";
import {OpeningWithSelected} from "../openings/pdf/OpeningsTab";
import {DiscontinuedWarning} from "./DiscontinuedWarning";
import {useSnackbar} from "../../../../misc/Snackbar";
import {matchesPhaseFilter, minPhase, phaseId, sortByPhase} from "../../../../misc/Phase";

export type HWFilter = {
    opening: string;
    code: string;
    finish: string;
    dimensions: string;
    activeQty: string;
    inactiveQty: string;
    commonQty: string;
    note: string;
    phases: string[];
};

const blankFilter = {
    opening: "",
    code: "",
    finish: "",
    dimensions: "",
    activeQty: "",
    inactiveQty: "",
    commonQty: "",
    note: "",
    phases: [] as string[],
};

export const qtyWidth = 60;

export function convertHardwareForViewMode(data: HardwareGroupWithContent[], viewMode: ViewMode, openings: OpeningWithSelected[]) {
    if(viewMode === "default") {
        return data
    }

    let result: HardwareGroupWithContent[] = [];

    // no explicit selection (e.g. the main list) means show every opening
    const includeAllOpenings = openings.length === 0;

    data.map(d => {
        d.openings.filter(op => includeAllOpenings || openings.find(o => o.id === op.id && o.selected))
            .map(op => {
                result.push(Object.assign(scopeHardwareToOpenings(d, [op]), {
                    id: -(d.id + 1/op.id), // fractional id to preserve uniqueness, but cause errors if someone tries to submit it
                }))

            return null;
        })

        return null;
    })

    orderByAscending(result, item => item.openings[0].seqNumber)
    return result;
}

export function filterHardwareByPhase(data: HardwareGroupWithContent[], phases: string[]): HardwareGroupWithContent[] {
    if(phases.length === 0) return data;

    return data.flatMap(d => {
        const openings = d.openings.filter(op => matchesPhaseFilter(op.phase, phases));
        if(openings.length === 0) return [];
        if(openings.length === d.openings.length) return [d];
        return [scopeHardwareToOpenings(d, openings)];
    });
}

export function scopeHardwareToOpenings(d: HardwareGroupWithContent, openings: HwOpening[]): HardwareGroupWithContent {
    const ids = openings.map(op => op.id);

    return Object.assign({}, d, {
        openings: openings,
        hardware: d.hardware.map(h => {
            // re-scope each dimensioned qty to just these openings using the per-opening
            // breakdown in openingDetail (the group-level active/inactive/common are
            // totals across every opening in the group and would over-count here).
            const calcByDimension = h.calcByDimension ? h.calcByDimension
                .map(c => {
                    if(!c.openingDetail) {
                        // no per-opening breakdown available, fall back to an even split
                        return Object.assign({}, c, {
                            qty: c.qty * openings.length / d.openings.length,
                            activeQty: c.activeQty * openings.length / d.openings.length,
                            inactiveQty: c.inactiveQty * openings.length / d.openings.length,
                            commonQty: c.commonQty * openings.length / d.openings.length,
                        });
                    }

                    const detail = c.openingDetail.filter(od => ids.includes(od.opening));
                    if(detail.length === 0) return null; // dimension doesn't apply to these openings

                    const sumBy = (subType: string) => detail
                        .filter(od => od.subType === subType)
                        .reduce((sum, od) => sum + od.qty, 0);

                    const activeQty = sumBy("active");
                    const inactiveQty = sumBy("inactive");
                    const commonQty = sumBy("common");

                    return Object.assign({}, c, {
                        qty: activeQty + inactiveQty + commonQty,
                        activeQty,
                        inactiveQty,
                        commonQty,
                        openingDetail: detail,
                    });
                })
                .filter((c): c is NonNullable<typeof c> => c !== null) : undefined;

            const calcTotalQty = calcByDimension
                ? calcByDimension.reduce((sum, c) => sum + c.activeQty + c.inactiveQty + c.commonQty, 0)
                : h.calcTotalQty * openings.length / d.openings.length;

            return Object.assign({}, h, {
                calcByDimension,
                calcTotalQty,
            });
        })
    });
}

export function WorkingHardware(props: {
    onFilter(str: string, hidePending: boolean): void
}) {
    const project = useProjectId();
    const hapi = useHwAPI();
    const alternative = useAlternative();
    const previewChangeset = useContext(ChangePreviewContext);
    const snack = useSnackbar();
    const data = useAsync2(async (input) => {
            try {
                snack.loading();
                const result = await hapi.listWithContents(input)
                snack.hide();
                return result;
            } catch (e: any) {
                snack.error(e.toString())
                throw e;
            }
        },
        {
            project: project,
            alternative: alternative,
            previewChangeset: previewChangeset.enabled ? previewChangeset.sessionId : undefined
        },
        [project, hapi, alternative, previewChangeset.enabled, previewChangeset.sessionId]);

    useReload(data.reload, [data.reload]);

    const viewCtx = useContext(HardwareViewModeContext);
    const setPhaseOptions = viewCtx.setPhaseOptions;
    const phaseOptions = useMemo(() => (data.asList || []).flatMap(g => g.openings.map(o => o.phase)), [data.asList]);
    useEffect(() => {
        setPhaseOptions(phaseOptions);
    }, [phaseOptions, setPhaseOptions]);

    // a phase in the url that no opening has is ignored
    const phaseFilter = viewCtx.phaseFilter.filter(id => phaseOptions.some(p => phaseId(p) === id));

    const [rawFilter, setFilter] = useState<HWFilter>(blankFilter);
    const filter = Object.assign({}, rawFilter, {phases: phaseFilter});

    const filterEn = !!(filter.opening || filter.code || filter.finish || filter.dimensions || filter.activeQty || filter.inactiveQty || filter.commonQty || filter.note || filter.phases.length > 0);
    const filterOpening = filter.opening;
    const onFilter = props.onFilter;
    useEffect(() => {
        onFilter(filterOpening, filterEn && !filterOpening);
    }, [filterEn, onFilter, filterOpening])

    const editCtx = useContext(HwEditingContext);
    const viewMode = useContext(HardwareViewModeContext).viewMode;
    const list = useMemo(() => {
        const result = convertHardwareForViewMode(data.asList, viewMode, []); // TODO: add openings?
        return sortByPhase(result, g => minPhase(g.openings.map(o => o.phase)));
    }, [data.asList, viewMode]);

    useEffect(() => {
        const hash = window.location.hash;
        if(!hash) return;

        const id = hash.replace("#", "");

        const int = setInterval(() => {
            const el = document.getElementById(id)
            if(!el) return;

            el.scrollIntoView({
                block: "start",
            });

            clearInterval(int);
        }, 100);

        const tm = setTimeout(() => {
            clearInterval(int);
        }, 2000);

        return () => {
            clearTimeout(tm);
            clearInterval(int);
        }
    }, [data.asList, editCtx.editing]);

    const [createNeedsLocationConfirmation, setCreateNeedsLocationConfirmation] = useState<number>();

    return (
        <div>
            <DiscontinuedWarning data={data.asList} />
            <table className={tableClass}>
                <thead>
                <tr>
                    <th />
                    <th style={{width: 100}} colSpan={2}>Opening</th>
                    <th style={{width: 170}}>Code</th>
                    <th style={{width: 100}}>Finish</th>
                    <th style={{width: 100}}>Dimensions</th>
                    <th style={{width: qtyWidth}}>
                        <Tooltip title="Active Quantity">
                            <div>A.Qty</div>
                        </Tooltip>
                    </th>
                    <th style={{width: qtyWidth}}>
                        <Tooltip title="Inactive Door Quantity">
                            <div>I.Qty</div>
                        </Tooltip>
                    </th>
                    <th style={{width: qtyWidth}}>
                        <Tooltip title="Common Quantity (thresholds, seals etc)">
                            <div>C.Qty</div>
                        </Tooltip>
                    </th>
                    <th></th>
                    <th style={{width: "auto"}}>Note</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td />
                    <EditCell inline inputStyle={{width: 100}} colSpan={2} onChange={v => setFilter(Object.assign({}, filter, {
                        opening: v,
                    }))} value={filter.opening} placeholder="Filter" />
                    <EditCell inline inputStyle={{width: 100}} onChange={v => setFilter(Object.assign({}, filter, {
                        code: v,
                    }))} value={filter.code} placeholder="Filter" />
                    <EditCell inline inputStyle={{width: 100}} onChange={v => setFilter(Object.assign({}, filter, {
                        finish: v,
                    }))} value={filter.finish} placeholder="Filter" />
                    <EditCell inline inputStyle={{width: 100}} onChange={v => setFilter(Object.assign({}, filter, {
                        dimensions: v,
                    }))} value={filter.dimensions} placeholder="Filter" />
                    <EditCell inputStyle={{width: qtyWidth}} inline onChange={v => setFilter(Object.assign({}, filter, {
                        activeQty: v,
                    }))} value={filter.activeQty} placeholder="Filter" />
                    <EditCell inputStyle={{width: qtyWidth}} inline onChange={v => setFilter(Object.assign({}, filter, {
                        inactiveQty: v,
                    }))} value={filter.inactiveQty} placeholder="Filter" />
                    <EditCell inputStyle={{width: qtyWidth}} inline onChange={v => setFilter(Object.assign({}, filter, {
                        commonQty: v,
                    }))} value={filter.commonQty} placeholder="Filter" />
                    <td></td>
                    <EditCell inputStyle={{width: "100%"}} inline onChange={v => setFilter(Object.assign({}, filter, {
                        note: v,
                    }))} value={filter.note} placeholder="Filter" />
                </tr>
                {data.loadingOrError && <tr key="loading"><td colSpan={colCount}>{data.LoadingOrErrorElement}</td></tr>}
                {list.filter(l => {
                    if(editCtx.creating) return false;
                    if(editCtx.editing && editCtx.editing.id !== l.id) return false;
                    return true;
                }).map(l => (
                    <HardwareGroup
                        hasFilter={filterEn} filter={filter} key={l.id} value={l}
                        createNeedsLocationConfirmation={createNeedsLocationConfirmation === l.id}
                        onCreateNeedsLocationConfirmation={setCreateNeedsLocationConfirmation}
                    />
                ))}
                {editCtx.creating && <HardwareGroup
                    hasFilter={false} filter={blankFilter} key="create-item"
                    value={editCtx.creating}
                    isCreate
                    onCreateNeedsLocationConfirmation={setCreateNeedsLocationConfirmation}
                />}
                </tbody>
            </table>
        </div>
    )
}